import { Camera } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { predictTestNumber } from '../../data/huggingface';
import CameraCapture from '../../components/CameraCapture';
import {
  addUploadInProgress,
  removeUploadInProgress
} from '../../Redux/userSlice';
import { addOvulationTestToSelf } from '../../Redux/calendarDataSelfSlice';
import { addOrUpdateSelfTest } from '../../Redux/store';
import { setTestViewer } from '../../Redux/uiSlice';
import { generateImageUuid } from '../../utils/imageUrls';
import './CalendarBottomMenu2.css';

// Upload image to Supabase storage and return JSONB metadata
async function uploadImage(userId, file) {
  const imageUUID = generateImageUuid();
  const path = `${userId}/${imageUUID}`;
  const { data: uploadData, error: uploadErr } = await supabase.storage
    .from('test-images')
    .upload(path, file, { upsert: false });

  if (uploadErr) {
    console.error('[uploadImage] Upload error:', uploadErr);
    throw uploadErr;
  }

  // Return JSONB object with metadata (loading: true during upload)
  return {
    imageUUID,
    source: 'camera',
    userId,
    bucket: 'test-images',
    loading: true
  };
}

// Create test row in database
async function createTestRow(userId, photoUrl, takenAt) {
  const { data: insertData, error: dbErr } = await supabase
    .from('ovulation_tests')
    .insert({ user_id: userId, photo_url: photoUrl, value: null, taken_at: takenAt })
    .select()
    .single();

  if (dbErr) {
    console.error('[createTestRow] DB error:', dbErr);
    throw dbErr;
  }

  return insertData;
}

function AddTestPictureButton() {
  const dispatch = useDispatch();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [currentTestUpload, setCurrentTestUpload] = useState(null);
  const userId = useSelector((s) => s.user.authUserId);
  const calendarData = useSelector((s) => s.calendar.data);

  const handleCapture = useCallback(async (file) => {
    setCameraOpen(false);
    if (!userId) return;

    const startTime = new Date().toISOString();

    // Create object URL for display (File objects can't go in Redux)
    const fileUrl = URL.createObjectURL(file);

    // Initialize local state with placeholder (keeps File reference for upload)
    const initialUpload = {
      test: {
        file,
        fileUrl,
        taken_at: startTime,
        value: null,
      },
      uploading: true,
    };
    setCurrentTestUpload(initialUpload);
    // Redux gets serializable data only (no File object)
    dispatch(setTestViewer({
      test: {
        fileUrl,
        taken_at: startTime,
        value: null,
      },
      uploading: true,
    }));

    const uploadId = crypto.randomUUID();
    dispatch(addUploadInProgress({
      id: uploadId,
      startTime,
      status: 'uploading',
    }));

    try {
      // Upload image
      const photoUrl = await uploadImage(userId, file);

      // Update local and Redux state with photo URL (still loading)
      const withPhotoUrl = {
        test: { ...initialUpload.test, photo_url: { ...photoUrl, loading: true } },
        uploading: true,
      };
      setCurrentTestUpload(withPhotoUrl);
      dispatch(setTestViewer({
        test: { fileUrl, photo_url: { ...photoUrl, loading: true }, taken_at: startTime, value: null },
        uploading: true,
      }));

      // Create test row (with loading: false since upload is complete)
      const finalPhotoUrl = { ...photoUrl, loading: false };
      const insertData = await createTestRow(userId, finalPhotoUrl, startTime);

      // Update state with test ID
      const testData = {
        id: insertData.id,
        photo_url: finalPhotoUrl,
        value: null,
        taken_at: startTime,
      };

      const finalUpload = {
        test: { ...withPhotoUrl.test, ...testData },
        uploading: false,
      };
      setCurrentTestUpload(finalUpload);
      // Revoke the object URL since we now have the real photo_url
      URL.revokeObjectURL(fileUrl);
      dispatch(setTestViewer({
        test: { ...testData },
        uploading: false,
      }));

      // Add to Redux (calendarDataSelf for calendar view)
      dispatch(addOvulationTestToSelf({ test: testData }));
      // Add to Redux (ovulationTests for Tests list view)
      dispatch(addOrUpdateSelfTest(testData));
      dispatch(removeUploadInProgress(uploadId));

      // Run AI prediction
      predictTestNumber(file)
        .then((responseData) => {
          // Extract value from response, use -1 if failed
          const finalValue = responseData?.value ?? -1;
          const updatedTest = { ...testData, value: finalValue, prediction_data: responseData };
          dispatch(addOvulationTestToSelf({ test: updatedTest }));
          dispatch(addOrUpdateSelfTest(updatedTest));

          // Update Supabase with both value and prediction_data
          supabase
            .from('ovulation_tests')
            .update({ value: finalValue, prediction_data: responseData })
            .eq('id', insertData.id)
            .then(({ error }) => {
              if (error) {
                console.error('[handleCapture] AI value update error:', error);
              }
            });
        })
        .catch((error) => {
          console.error('[handleCapture] AI prediction error:', error);
          // On error, set value to -1 and prediction_data to null
          const updatedTest = { ...testData, value: -1 };
          dispatch(addOvulationTestToSelf({ test: updatedTest }));
          dispatch(addOrUpdateSelfTest(updatedTest));
          supabase
            .from('ovulation_tests')
            .update({ value: -1, prediction_data: null })
            .eq('id', insertData.id)
            .then(({ error }) => {
              if (error) {
                console.error('[handleCapture] AI error update error:', error);
              }
            });
        });

    } catch (error) {
      console.error('[handleCapture] Upload failed:', error);
      dispatch(removeUploadInProgress(uploadId));
      const errorUpload = {
        test: initialUpload.test,
        uploading: false,
        error: error.message,
      };
      setCurrentTestUpload(errorUpload);
      URL.revokeObjectURL(fileUrl);
      dispatch(setTestViewer({
        test: { fileUrl, taken_at: startTime, value: null },
        uploading: false,
        error: error.message,
      }));
    }
  }, [userId]);

  return (
    <>
      <div className="center-circle-front" onClick={() => setCameraOpen(true)}>
        <div className="center-inner-circle">
          <Camera className="center-camera-icon" />
          <div className="center-label-rect">Add Test Picture</div>
        </div>
      </div>

      {cameraOpen && (
        <CameraCapture
          aspectW={6}
          aspectH={1}
          outputWidth={384}
          onCapture={handleCapture}
          onCancel={() => setCameraOpen(false)}
        />
      )}
    </>
  );
}

export default AddTestPictureButton;
